import { lowerCase } from 'lodash-es';
import { useEffect, useState } from 'react';

import { PreAnnotationModal } from '@/component';
import { TASK_CONST } from '@/constant';
import {
  useRequestFramePreAnnotationMutation,
  useUpdateFrameWorkStateMutation,
} from '@/hook';
import { AiAnno, RocketLaunch, VButton, VFlex, useShortcut } from '@/package';
import {
  useLabelingBoxStore,
  useTaskFrameStore,
  useToolPermissionStore,
  useToolSettingStore,
} from '@/store';
import type { BoxWithLabel, Modeling } from '@/type';

const TaskLayoutHeaderNotStartedFrame = () => {
  const { currentFrame, selectedTaskId, isValidFrameDetail, selectedTask } =
    useTaskFrameStore();
  const { namespace, register, unregister } = useShortcut();
  const { permission } = useToolPermissionStore();
  const { setBoxList } = useLabelingBoxStore();
  const [preAnnotationModalVisible, setPreAnnotationModalVisible] =
    useState(false);
  const currentFrameSeq = currentFrame!.frameSeq;
  const trackSession =
    currentFrame?.usableTrackSession ?? currentFrame?.curTrackSession;
  const { isDirectWork } = useToolSettingStore();

  const { mutateAsync: runPreAnnotation } =
    useRequestFramePreAnnotationMutation();
  const { mutateAsync: updateFrameWorkState } =
    useUpdateFrameWorkStateMutation();

  const onPreAnnotationButtonClick = () => {
    setPreAnnotationModalVisible(true);
  };

  const onConfirm = (modelingID: Modeling['id'], useTrackSession: boolean) => {
    return runPreAnnotation({
      modelingID,
      pFrameID: currentFrameSeq,
      pTaskID: selectedTaskId!,
      useSession: useTrackSession,
    })
      .then(res => {
        setBoxList(
          res.data.resultData.workData.map(
            v =>
              ({
                ...v,
                label: lowerCase(v.label),
              }) as BoxWithLabel,
          ),
        );
      })
      .finally(() => {
        setPreAnnotationModalVisible(false);
      });
  };

  const onStartButtonClick = () => {
    if (!isValidFrameDetail()) return;

    updateFrameWorkState({
      frameSeq: currentFrameSeq,
      taskID: selectedTaskId!,
      currentWorkState: currentFrame!.workState,
      workState: TASK_CONST.TASK_FRAME_WORKSTATE.WORKING,
    }).catch(error => {
      console.error('Failed to update work state:', error);
    });
  };

  useEffect(() => {
    register('labeling.start.frame', onStartButtonClick);

    return () => {
      unregister('labeling.start.frame');
    };
  }, [currentFrameSeq, namespace]);

  useEffect(() => {
    if (isDirectWork) {
      onStartButtonClick();
    }
  }, [isDirectWork, currentFrameSeq]);

  return (
    <>
      <VFlex
        className="h-[36px] w-[408px] px-1"
        justify="center"
        items="center"
      >
        <VFlex gap={0.25} className="size-full">
          {permission.preAnnotation.acceptable && (
            <VButton
              className="h-9! flex-1 text-primary-500!"
              style={{
                background:
                  'linear-gradient(121deg, #648DFF 10.35%, #00B4D8 43.96%, #B384FF 83.16%)',
                backgroundSize: '300% 300%',
                animation: 'tool-ai_anno-gradient 3s ease-in-out infinite',
              }}
              onClick={() => onPreAnnotationButtonClick()}
            >
              <AiAnno className="size-7" />
              <div className="text-body-lg font-semibold">AI-anno</div>
            </VButton>
          )}

          {permission.labeling.acceptable && (
            <VButton
              className="h-9! flex-1"
              onClick={() => onStartButtonClick()}
            >
              <RocketLaunch className="size-7" />
              <div className="text-body-lg font-semibold">Get started</div>
            </VButton>
          )}
        </VFlex>
      </VFlex>
      {selectedTask && (
        <PreAnnotationModal
          usableTrackSession={trackSession}
          task={selectedTask}
          enabledTrackSession
          visible={preAnnotationModalVisible}
          setVisible={setPreAnnotationModalVisible}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
};

export default TaskLayoutHeaderNotStartedFrame;
